import React, {ReactElement, useContext, useState} from 'react';
import {errorSink} from '../ErrorSink';
import {FormContext, FormContextType} from './MDForm';
import {DeclaredComponent} from '../DynamicComponent';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import {YearPicker} from '../fields/YearPicker';
import {propsToCSS} from '../css';
import {CodeRefEnum} from '../fields/CodeRefEnum';
import {Checkbox} from '../fields/Checkbox';
import {BASIC_ELEMENT_SCHEMA, BasicElementProps} from '../layout/Group';
import {merge} from 'lodash';
import {DatePicker} from '../fields/DatePicker';
import Row from "antd/lib/grid/row";
import Col from "antd/lib/grid/col";
import {coerceToBoolean} from './coercer';

import {SelectAppUpdater} from '../fields/SelectAppUpdater';
import {CheckToggle} from '../fields/CheckToggle';

import {TooltipHint} from './TooltipHint';
import {ApplicationActionsContext} from "../data/ApplicationDataSource";
import {Attribute, AttributeCondition} from "./api/attribute";
import {FieldControlProps} from "./fieldControlProps";
import {FormUIConfigContext} from "./formUIContext";
import { FormValidationContext } from '../../context/FormValidationProvider';
import { AsYouType } from 'libphonenumber-js';
import {SelectScheduledItem} from '../fields/SelectScheduledItem';
import { GlobalStateContext } from '../../context/GlobalStateProvider';

export type FieldProps = {
  attr: string,
  dataType: string,
  label: ReactElement|string,
  component: any,
  value: any,
  hintText: string,
  autoSave: boolean,
  resetDependentFields: string[],
  hidden: boolean,
  showTopLabel: boolean,
  props: any,

  // it's used(being read) indirectly by MDForm
  defaultValueMapping: string,

} & BasicElementProps;

export function isApplicable(attrId, pathPrefix=undefined) {
  const formContext = useContext(FormContext);
  const attr: Attribute = formContext.metadata[attrId];
  let foundIt = false;

  if (attr && attr.attributeRelationships) {
    for (let attrRltn of attr.attributeRelationships) {
      if (attrRltn.typeCd === 'ApplicableIf' && attrRltn.attributeConditions) {
        foundIt = true;
        if (!formContext.evalConditions(attrRltn.attributeConditions, true, pathPrefix)) {
          return false;
        }
      }
    }
  }
  return true;
}

export function Field({attr: attrId, dataType, label, component, hintText, defaultValueMapping, autoSave = false, resetDependentFields, hidden = false, showTopLabel = false,
                        ...props}: FieldProps) {

  const [domId] = useState(() => `${attrId}:${COUNTER++}`);
  const formContext = useContext(FormContext);
  const actions = useContext(ApplicationActionsContext);
  const cfg = useContext(FormUIConfigContext);
  const formValidationContext = useContext(FormValidationContext);
  const { isDesktop} = useContext(GlobalStateContext);

  const attr: Attribute = formContext.metadata[attrId];

  if (!attr) {
    return errorSink(`attr '${attrId}' doesn't exist`, attrId);
  }

  const {touched, errors} = formContext.getStatus(attrId);

  if ( ! isApplicable(attrId)) {
    return null;
  }
  if (hidden) {
    return null;
  }

  if (!label) {
    label = attr.label;
  }

  let isYesOrNoToggle = false;
  if (component){
    for (let k in component){
      if (k === 'YesNoToggle' || k === 'YesNoRadio'){
        isYesOrNoToggle = true;
      }
    }
  }

  let overriddenLabel= label;
  if (attr.requiredInd) {
    label = <>{label} <span>*</span></>
  }

  const onChangeOrClick = (fieldValue) => {
    formContext.setValueByAttr(attrId, fieldValue);
    if (autoSave) {
      actions.saveApplication();
    }
    if(resetDependentFields){
      resetDependentFields.forEach((field)=> {
        if(formContext.getValueByAttr(field) !== undefined){
          let fieldMapping = formContext.metadata[field].mapping;
          formContext.setValue(fieldMapping,undefined);
          formContext.touch(field,false);
        }
      });
    }
  };

  props = {
    value: formContext.getValueByAttr(attrId),
    checked: coerceToBoolean(formContext.getValueByAttr(attrId)),
      onChange: eventOrValue => {
        let fieldValue = universalGet(eventOrValue);
        if (attr.dataType === 'Phone' && fieldValue.length >= 5) {
            fieldValue = new AsYouType('US').input(fieldValue);
        }
        onChangeOrClick(fieldValue);
      },
    ...propsToCSS(props),
    id: domId,
    label:overriddenLabel,
    ...props
  };

  let fieldImpl;

  if (component) {
    fieldImpl = (
      <DeclaredComponent declaration={component} {...props} />
    );
  } else {
    fieldImpl = getComponentForField(dataType, attr, props);
  }

const hasErrors = touched && errors && errors.length;

  //check if it is a checkbox construct row field differently
  if(fieldImpl.type.name === 'Checkbox'){
    return (
        <Row {...cfg.row} className={'checkbox-input'}>
              {fieldImpl}
          <Col className={'field-label-col'}>
                {label}
                {hintText && <TooltipHint style={{marginLeft: '1em'}} hintText={hintText}/>}
          </Col>
      </Row>)
  }

  const LabelComponent = ({children})=>{
    return isYesOrNoToggle ? <label htmlFor={domId} aria-label={'Select ' + overriddenLabel}>{children}</label>
      : <label htmlFor={domId}>{children}</label>
  }

  return (
    <Row {...cfg.row}>
      {!cfg.noLabels && <Col className={'field-label-col'}{...cfg.label}>
        <LabelComponent>
          {label}

          {hintText && <TooltipHint style={{marginLeft: '1em'}} hintText={hintText}/>}
        </LabelComponent>
      </Col> }
      <Col {...cfg.control} span={!cfg.noLabels ? 12: 24}>
        {showTopLabel &&
          <LabelComponent>
            {label}

            {hintText && <TooltipHint style={{marginLeft: '1em'}} hintText={hintText}/>}
          </LabelComponent>
        }
        <div className={hasErrors ? 'has-error' : ''}>{fieldImpl}</div>
        {hasErrors && errors.map(err => <div role={"alert"} className='fieldError' key={err}>{err}</div>)}
      </Col>
    </Row>
  )
}
/* reference:  https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles */
/*
 * TODO: support the other attributeConditoins and combinations of them.  Currently only simple IF and OR are supported with operator EQUAL
 */


function getComponentForField(dataType: string, attr: Attribute, props: FieldControlProps<any>) {
  let value = props.value;
  if (attr.productListConstraint || attr.globalListConstraint) {
    if (dataType === 'SelectUpdate') {
      return <SelectAppUpdater attr={attr} {...props} />;
    }
    if (dataType === 'SelectScheduledItem') {
      return <SelectScheduledItem attr={attr} {...props} />;
    }
    return <CodeRefEnum attr={attr} {...props} />;
  }

  let ariaLabel = attr.label;
  if(attr.dataType === 'Date'){
    ariaLabel = 'Select ' + attr.label;
  }
  let Component;
  dataType = dataType ? dataType : attr.dataType;
  Component = getComponentByDataType(dataType);

  if (dataType === 'Money') {
    return (
      <Component
        aria-label={ariaLabel + ': Money amount'}
        value={value !== undefined ? value : ''}
        formatter={value => {
          if(isNaN(value)){
            return '$0.00';
          }
          else{
            return  `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        }}
        parser={value => value.replace(/\$\s?|(,*)/g, '')}
        precision={2}
        {...props}
      />
    )
  }
  if (dataType === 'DayNumber') {
    return (
      <Component
        aria-label={ariaLabel + ': Day as number'}
        value={value !== undefined ? value : ''}
        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={value => value.replace(/\$\s?|(,*)/g, '')}
        max={31}
        min={1}
        {...props}
      />
    )
  }

  if (dataType === 'Integer') {
      return (
          <InputNumber
              aria-label={ariaLabel + ': Integer number'}
              value={value !== undefined ? value : ''}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              {...props}
          />
      )
  }

  if (dataType === 'Limit') {
    return (
        <Component
            aria-label={ariaLabel + ': Limit amount'}
            value={value !== undefined ? value.toString() : ''}
            formatter={value => {
              if(isNaN(value)){
                return '';
              }
              else{
                return  `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              }
            }}
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            {...props}
        />
    )
  }

  return (
    <Component
      value={value !== undefined ? value : ''}
      ariaLabel={ariaLabel}
      {...props}
    />
  )
}

function getComponentByDataType(dataType) {
  if (dataType === 'String') {
    return Input;
  } else if (dataType === 'Year') {
    return YearPicker;
  } else if (dataType === 'Boolean') {
    return Checkbox;
  } else if (dataType === 'Date') {
    return DatePicker;
  } else if (dataType === 'Integer') {
    return InputNumber;
  } else if (dataType === 'Money') {
    return InputNumber;
  } else if (dataType === 'DayNumber') {
    return InputNumber;
  } else if (dataType === 'CheckToggle') {
    return CheckToggle;
  } else if (dataType === 'Limit') {
    return InputNumber;
  }

  return Input;
}

function universalGet(eventOrValue) {
  return eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
}

Field.schema = merge({
  properties: {
    attr: {
      type: 'string',
    },
    label: {
      type: 'string',
    },
  }
}, BASIC_ELEMENT_SCHEMA);

let COUNTER = 0;